// Help & Support Articles
// Written in plain, friendly language for all users.
// Aug 2026 — fully aligned with the Specialist Tutor Library business model:
// 9 topic-locked AvA tutors, teens customise (never create), Hearts store.

export const helpCategories = [
  // Row 1
  { id: 'getting-started', name: 'Getting Started', icon: 'rocket', color: 'blue', description: 'Learn the basics of using HiBFF' },
  { id: 'ava-tutors', name: 'Your AvA Tutor Team', icon: 'graduation', color: 'indigo', description: 'Meet your 9 specialist tutors and make them yours' },
  { id: 'dashboard', name: 'Understanding Your Dashboard', icon: 'layout', color: 'purple', description: 'Learn what everything on your main screen means' },
  { id: 'challenges', name: 'Challenges & Progress', icon: 'target', color: 'orange', description: 'Your 20-challenge journey, streaks and badges' },
  // Row 2
  { id: 'hearts', name: 'Hearts & Rewards', icon: 'heart', color: 'rose', description: 'Your time credits for AvA conversations and extras' },
  { id: 'groups', name: 'Groups & Community', icon: 'users', color: 'green', description: 'Connect with others who understand' },
  { id: 'voice', name: 'Voice & Video', icon: 'mic', color: 'teal', description: 'Talking out loud with your animated tutors' },
  { id: 'privacy', name: 'Privacy & Safety', icon: 'shield', color: 'red', description: 'How we protect you and keep your information safe' },
  // Row 3
  { id: 'subscription', name: 'Subscription & Billing', icon: 'credit-card', color: 'yellow', description: 'Plans, pricing, and how to manage your subscription' },
  { id: 'parents', name: 'For Parents', icon: 'family', color: 'pink', description: 'Parent controls and teen safety features' },
  { id: 'technical', name: 'Technical Help', icon: 'tool', color: 'gray', description: 'Troubleshooting and technical support' },
  { id: 'contact', name: 'Contact & Support', icon: 'mail', color: 'sky', description: 'Get help from our support team' }
];

export const helpArticles = [
  // CATEGORY: Getting Started
  {
    id: 'what-is-hibff',
    category: 'getting-started',
    title: 'What is HiBFF?',
    content: `**HiBFF** stands for **Hi Best Friend Forever** — a social intelligence platform designed to help you build real-world social skills. Think of it as a safe space to practise conversations, build confidence, and prepare for real-life interactions.

You practise with **AvAs** — AI tutors created by HiBFF. Every teen gets the full team of **9 specialist tutors**, and each one is an expert in one part of teen life: your guide Maya, your challenge friend Neo, your knowledge tutor Rebecca, and six topic-locked specialists covering relationships, careers, friendship dynamics, public speaking, organization and online life.

Unlike a regular chatbot, your AvAs adapt to your style. Every AvA is calibrated by a friendly 22-question OCEAN personality assessment, so conversations match how you actually think and feel.

**HiBFF exists to help you need it less** — it is a practice tool, not a replacement for real human connection.`,
    relatedArticles: ['meet-your-tutors', 'talk-to-ava']
  },
  {
    id: 'talk-to-ava',
    category: 'getting-started',
    title: 'How do I talk to my AvAs?',
    content: `Talking to an AvA is just like texting a friend:

**Step 1:** Go to "My Practice Sessions" and click the AvA you want to practise with.

**Step 2:** Type your message in the box at the bottom of the screen.

**Step 3:** Press Enter or click the Send button.

Your AvA will respond within a few seconds — in text, voice, or both, depending on the Interaction Mode you pick at the top of the chat.

**Tips for better conversations:**
- Be honest about how you are feeling
- Do not worry about saying the "right" thing
- If your AvA does not understand something, just explain it differently
- You can rehearse the same situation as many times as you need to

**Want to talk out loud instead of typing?** Tap the microphone icon and speak — your AvA listens and answers back with voice.`,
    relatedArticles: ['what-is-hibff', 'voice-chat']
  },

  // CATEGORY: Your AvA Tutor Team
  {
    id: 'meet-your-tutors',
    category: 'ava-tutors',
    title: 'Who are my 9 AvA tutors?',
    content: `Every HiBFF teen gets the full team of nine tutors — each one an expert in one part of your life:

- **Maya — Your Guide.** She sets everything up, explains how HiBFF works, and is always in your dashboard.
- **Rebecca — Your Knowledge Tutor.** Science and tech, taught Socratic-style so you find the answers yourself.
- **Neo — Your Practice Friend.** Your companion through the 20 social fitness challenges.
- **Leo — Relationship Communication.** Crushes, dating and difficult messages, rehearsed safely.
- **Marcus — Career & Authority.** Job interviews and speaking up respectfully with teachers.
- **Chloe — Social Dynamics & Conflict.** Friend drama, group projects and standing your ground kindly.
- **Ethan — Public Speaking & Voice.** Presentations without the panic.
- **Sarah — Organization & Boundaries.** Workload, time management, and saying no without guilt.
- **Petra — Digital Life & Online Safety.** Group chat drama, cyberbullying, and staying in control online.

You cannot create new AvAs from scratch — HiBFF builds and safety-checks every tutor, so your parents know exactly who you are talking to. But you can make every tutor **yours** — see "How do I customise my AvA tutors?"`,
    relatedArticles: ['create-ava', 'topic-locked-tutors']
  },
  {
    id: 'create-ava',
    category: 'ava-tutors',
    title: 'How do I customise my AvA tutors?',
    content: `Every tutor (except Maya and Rebecca, who are fixed) can be customised to feel like YOUR friend:

**Step 1:** Click "My Practice Sessions" in the menu on the left side of your screen.

**Step 2:** Find the tutor you want to change and tap the **pencil icon** in the top corner of their picture.

**Step 3:** Pick a new look from the image library — it appears in the preview on the right.

**Step 4:** Give them a new name if you like, choose their voice, pick a practice style, and even write a short backstory.

**Step 5:** Click **Save** — your conversation with your freshly-customised tutor opens straight away.

**Important:** customising changes how a tutor looks and sounds, never what they know. Sarah is still your organization expert even if you rename her — that expertise is locked in for your safety.`,
    relatedArticles: ['meet-your-tutors', 'topic-locked-tutors']
  },
  {
    id: 'topic-locked-tutors',
    category: 'ava-tutors',
    title: 'Why does my tutor only talk about their topic?',
    content: `Each specialist tutor is deliberately **topic-locked** — they are brilliant at one thing and politely decline everything else.

If you ask Ethan (public speaking) about your crush, he will kindly point you to Leo. If you ask Sarah (organization) to write your homework essay, she will help you *plan* it instead — doing the work for you is not what she is for.

**Why we built it this way:**
- Every conversation stays inside safety guardrails your parents can trust
- Deep expertise beats shallow everything — a tutor who does one thing does it brilliantly
- You always know exactly which tutor to go to for which situation

It is not your AvA being unhelpful — it is your AvA keeping the promise HiBFF made to you and your family.`,
    relatedArticles: ['meet-your-tutors', 'conversation-privacy']
  },

  // CATEGORY: Dashboard
  {
    id: 'dashboard-cards',
    category: 'dashboard',
    title: 'What do all the cards on my dashboard mean?',
    content: `When you log in, you see your main dashboard. Each card shows different information:

**My Trainers Card:**
Your AvA tutor team. Click "Chat" to start talking to any of them, or tap a portrait to see their animation up close.

**Quick Mood Check:**
This is where you tell HiBFF how you are feeling today on a scale of 1 to 5. Rating your mood every day helps you notice patterns over time.

**My Progress Card:**
This shows your activity - how many days in a row you have used HiBFF, how many conversations you have had, and your current streaks. These are just for motivation, not judgment.

**Groups I Belong To:**
Support groups you have joined. These are spaces where you can connect with other people who understand what you are going through.

**Today's Insight Card:**
A personalized message or tip based on your recent conversations and mood. This helps you reflect and learn.

**My Achievements:**
Badges and milestones you have earned. These celebrate your progress and commitment to personal growth.

**Upcoming Events:**
Any scheduled group meetings, challenges, or reminders you have set up.

**Crisis Support Card:**
This is always visible. If you are in immediate danger or having a crisis, click the red "Crisis Help" button to get help right away.`,
    relatedArticles: ['what-are-hearts', 'what-is-hibff']
  },

  // CATEGORY: Challenges & Progress
  {
    id: 'challenges-journey',
    category: 'challenges',
    title: 'What are the 20 Challenges?',
    content: `HiBFF's heart is a journey of **20 social fitness challenges** you take on with **Neo**, your Practice Friend.

Each challenge is a real-world social step — small enough to complete in a week, big enough to matter. You rehearse it safely with Neo first, then go do it for real.

**How it works:**

**Step 1:** Open Challenges from the Apps menu and see your current challenge.

**Step 2:** Chat with Neo to rehearse — he knows exactly which challenge you are on and coaches you through it.

**Step 3:** Do it in real life, then mark it complete.

**Step 4:** Clear challenges to hit real milestones — the biggest is Pivot Moment No. 10, aka **Graduation**.

Your plan tier decides how deep your journey goes: Challenger unlocks challenges 1-3 in full depth, The Elite and Master unlock all 20.`,
    relatedArticles: ['streaks-and-badges', 'meet-your-tutors']
  },
  {
    id: 'streaks-and-badges',
    category: 'challenges',
    title: 'How do streaks and badges work?',
    content: `**Streaks** count the days in a row you have practised. Even one short conversation keeps your streak alive.

Having a tough day? You can spend **50 Hearts** on a **Streak Freeze** to protect your streak — because life happens.

**Badges** celebrate milestones: your first conversation, a 7-day streak, completing challenges, cheering on friends, and more. You will find them in "My Achievements" on your dashboard.

**Scout Badges** are special awards along the challenge journey — each one marks a real-world social skill you have proven, not just time spent in the app.

None of this is a judgment. Streaks and badges exist for one reason: to help you keep showing up for yourself.`,
    relatedArticles: ['challenges-journey', 'what-are-hearts']
  },

  // CATEGORY: Hearts & Rewards
  {
    id: 'what-are-hearts',
    category: 'hearts',
    title: 'What are Hearts and how do I get them?',
    content: `Hearts are HiBFF's flexible extras currency — think of them as **time credits and treats** that sit alongside your plan.

**Ways to get Hearts:**
- Earn them through daily activity — mood check-ins, conversations, streaks and milestones
- Buy a Hearts pack in the **Hearts Store** (50, 200 or 500 Hearts) — tap "Ask my parent" and they approve the purchase
- Receive them as a **gift** from a friend or family member

**What can I spend Hearts on?**
- **Limited-Edition NFT Drops** — weekly rare collectibles for your Vault (50-200 Hearts each)
- **Streak Freeze** — 50 Hearts protects your daily-practice streak on a tough day
- **Cheer On a Friend** — stake Hearts on a friend's weekly goal; they win, you both win
- **Gift Hearts** — send Hearts to anyone on HiBFF as a boost

**Important:** Hearts never change how many AvA tutors you have or what they can do — that is set by your plan. Hearts are just the fun extras layer.`,
    relatedArticles: ['buying-gifting-hearts', 'streaks-and-badges']
  },
  {
    id: 'buying-gifting-hearts',
    category: 'hearts',
    title: 'How do I buy or gift Hearts?',
    content: `**Buying Hearts (teens):**

**Step 1:** Open **Hearts Store** from the menu.

**Step 2:** Pick a pack — 50, 200 (best value) or 500 Hearts.

**Step 3:** Tap **"Ask my parent"**. Your parent gets a request and approves the purchase from their side. You will see the Hearts land in your balance at the top of the dashboard.

**Gifting Hearts:**

Scroll down in the Hearts Store to **"Gift Hearts"**, choose the person and the amount, add a message if you like, and send. Gifts are a lovely way to boost a friend who is having a hard week.

**Parents** can also buy Hearts directly for their teen from the Parent Dashboard's Hearts Store.`,
    relatedArticles: ['what-are-hearts', 'parental-controls']
  },

  // CATEGORY: Groups
  {
    id: 'what-are-groups',
    category: 'groups',
    title: 'What are Support Groups?',
    content: `Support Groups are spaces where you can connect with other HiBFF users who are going through similar experiences.

**Groups are organized by topic, like:**
- Anxiety and Panic Attacks
- Exam and School Stress
- Friendship and Social Confidence
- Grief and Loss
- New School / Moving Home

**How do groups work?**

Groups can be:
- **Text-based:** Like a forum where you post messages and others respond
- **Live chat:** Real-time text conversations scheduled at specific times
- **Audio rooms:** Voice-only meetings (your camera stays off)

All groups are moderated, which means there is someone making sure conversations stay supportive and respectful.

**Are groups anonymous?**

You can choose. When you join a group, you decide whether to use your real name or a username. Many people prefer to stay anonymous and that is completely okay.`,
    relatedArticles: ['join-group']
  },
  {
    id: 'join-group',
    category: 'groups',
    title: 'How do I join a Support Group?',
    content: `**Step 1:** Click on "Groups" in the menu.

**Step 2:** Browse through available groups or search for a specific topic.

**Step 3:** Click on a group to read its description and see when it meets.

**Step 4:** Click "Join Group."

Some groups let you join immediately. Others require approval from the group moderator to make sure the group stays safe and focused.

Once you are in a group, you will get notifications when there are new posts or when a live session is starting.

**Can I leave a group if it is not right for me?**

Yes, absolutely. Click "Leave Group" anytime. There is no penalty and no one will know you left.`,
    relatedArticles: ['what-are-groups']
  },

  // CATEGORY: Voice & Video
  {
    id: 'voice-chat',
    category: 'voice',
    title: 'How does voice chat work?',
    content: `Every AvA tutor can talk with you out loud — practising with your voice is the closest thing to the real conversation.

**Step 1:** Open a chat with any tutor.

**Step 2:** At the top, pick your **Interaction Mode**: Text, Voice, or Both.

**Step 3:** Tap the microphone next to the message box and speak. Your tutor listens, thinks, and answers back in their own voice.

**Tips:**
- "Both" mode shows the words on screen while your tutor speaks — great for rehearsing tricky phrases
- Each tutor has their own voice, and you can change it any time from the customise page (pencil icon)
- Voice chat is included on all paid plans

Practising out loud feels strange for about two minutes — then it becomes the most powerful tool in HiBFF.`,
    relatedArticles: ['animated-avas', 'talk-to-ava']
  },
  {
    id: 'animated-avas',
    category: 'voice',
    title: 'Why do my AvA portraits move?',
    content: `You may notice your tutors gently breathing and blinking on your dashboard — every AvA portrait comes to life with a subtle animation.

**Where you will see it:**
- The "My Trainers" grid — every tutor tile animates
- Inside a chat — your tutor's portrait stays alive while you talk
- Tap any portrait to **enlarge** it and see the animation up close

**If you customise a tutor's picture**, HiBFF generates a fresh animation for the new look — it can take a couple of minutes to appear the first time, so do not worry if the new portrait starts out still.

The animations are generated safely by HiBFF from the portrait image — there are no real people in them.`,
    relatedArticles: ['create-ava', 'voice-chat']
  },

  // CATEGORY: Privacy & Safety
  {
    id: 'conversation-privacy',
    category: 'privacy',
    title: 'Who can see my conversations with my AvA?',
    content: `Your conversations are private. Here is exactly who can and cannot see them:

**Who CAN see:**
- Only you
- No one else, including HiBFF staff, unless you choose to share

**Who CANNOT see:**
- Your friends or family
- Other HiBFF users
- HiBFF employees (unless there is a safety emergency)

**Special case for teens (ages 13 to 17):**

If you are between 13 and 17 years old, your parents set up some monitoring when you created your account. Here is what they can see:
- Your overall mood scores (like 3 out of 5)
- General topics you talked about (like "school stress" or "friend problems")
- How much time you spend on HiBFF

**What parents CANNOT see:**
- The actual words you said in conversations
- Specific details of what you discussed
- Your journal entries
- Your private messages

You can see exactly what your parents can monitor by clicking "Parent Dashboard" in your menu.`,
    relatedArticles: ['crisis-help', 'parental-controls']
  },
  {
    id: 'child-data-safety',
    category: 'privacy',
    title: 'How does HiBFF keep my child\'s data safe?',
    content: `HiBFF is designed so your child can practice social skills without giving up their privacy. Before any message reaches our AI coach, a "Privacy Shield" automatically strips out personal details like names, school names, emails, postcodes and ID numbers, and replaces them with codes. The AI model only ever sees those codes, not who your child is or where they go to school.

We also follow children's privacy laws such as COPPA and GDPR. That means we check age at sign-up, ask for parent consent for teens, and give you tools to download or delete your child's data at any time. Most data is automatically removed after about 90 days as part of our retention policy.

On top of that, every AvA tutor is topic-locked, so the AI won't talk about diagnoses, medical issues, self-harm, politics or other adult topics. HiBFF is there only to help your child practice everyday conversations and confidence, not to act as a therapist or give serious life advice.`,
    relatedArticles: ['conversation-privacy', 'parental-controls']
  },
  {
    id: 'crisis-help',
    category: 'privacy',
    title: 'What if I am in a crisis or thinking about hurting myself?',
    content: `If you are in immediate danger, **click the red "Crisis Help" button** at the top of your screen right now.

This button is always visible no matter where you are in HiBFF. When you click it, you will see two options:

**Option 1: Call Crisis Hotline**
This connects you to a real person who is trained to help in crisis situations. It is free, confidential, and available 24 hours a day, 7 days a week.

**Option 2: Live Chat with Counselor**
If you prefer typing instead of talking on the phone, you can chat with a trained crisis counselor right away.

**What happens if I am a teenager and I click Crisis Help?**

Your parents will get a text message and email letting them know you needed crisis support. This is for your safety. The crisis counselor will still talk to you privately first.

**Important:** Your AvA cannot help with emergencies. AvAs are great for everyday support, but in a crisis you need a real human. That is why we have the Crisis Help button.

**Wherever you are in the world, you can also:**
- **US**: call or text **988** (Suicide and Crisis Lifeline) · call **911** in immediate danger
- **UK**: call **116 123** (Samaritans, free, 24/7) or **0800 1111** (Childline) · call **999** in immediate danger
- **Everywhere else**: visit **findahelpline.com** for free, confidential helplines in your country · call your local emergency number

You are not alone. There are people who want to help.`,
    relatedArticles: ['conversation-privacy']
  },

  // CATEGORY: Subscription
  {
    id: 'subscription-plans',
    category: 'subscription',
    title: 'What subscription plans does HiBFF offer?',
    content: `HiBFF has four subscription levels:

**FIRST STEPS (Free — a taste of HiBFF)**
- Meet 3 of our 9 AvAs — Rebecca (Knowledge Tutor), Chloe (Friendships) & Marcus (Careers & School)
- 10 FREE voice minutes to experience the magic of real conversation practice
- £0 forever, no card on file

**STEADY: £16/month**
- "Brave takes Courage"
- 6 of the 9 AvAs to practise with, including Neo — Your Friend (fully customisable image, voice & name)
- Challenges 1-10 (Virtual Badges)
- Unlimited text practice
- 45 voice minutes/month
- Confidence tracking + insights

**TOGETHER: £28/month**
- "Courage builds Confidence"
- All 9 AvAs — the complete tutor team
- All challenges 1-20 + AI-generated rewards in the 180-Day Vault
- 180 voice minutes/month
- Parent/Teen shared access (up to 2 teens)

**BRAVE: £188/year (Just £15.67/month billed annually)**
- "Confidence builds Character"
- Everything in Together
- 300 voice minutes/month (120 bonus minutes vs monthly)
- Priority support
- Early access to new features

**Got more than one teen at home?**

The first teen pays the full price above. Every additional teen gets **40% off** — so Together is £28/mo for teen one, then £16.80/mo for each additional teen.

**Can I try HiBFF before I pay?**

All paid plans come with a **30-day money-back guarantee** plus a 90-day pause option, and you can keep the free First Steps plan forever with no card on file.`,
    relatedArticles: ['cancel-subscription']
  },
  {
    id: 'cancel-subscription',
    category: 'subscription',
    title: 'How do I cancel or pause my subscription?',
    content: `**If a parent or guardian pays for your plan**, they manage it from their Parent Dashboard — Settings → Subscription. They have three options, all one tap with no lock-in:

**1. Cancel Subscription** — no more charges. You keep full access until the end of the paid period, then your account moves to the free First Steps tier. Everything you earned — Hearts, badges and Vault items — stays yours forever.

**2. Pause for 90 days** — a break with no charges. All your AvAs, conversations and progress are exactly where you left them when you come back.

**3. Pause indefinitely** — no time limit and no deadline. Your account becomes a safe archive for as long as it stays paused, and can be resumed any time.

**If you pay for your own plan** (adults), the same three options are in your own Settings → Subscription.

Nothing you've earned is ever taken away, whatever happens with the plan.`,
    relatedArticles: ['subscription-plans']
  },

  // CATEGORY: For Parents
  {
    id: 'parental-controls',
    category: 'parents',
    title: 'How do Parental Controls work?',
    content: `If your teenager (ages 13 to 17) signed up for HiBFF, you were asked to set up a parent account. Here is what you can control:

**A safe, fixed tutor library:**
Your teen practises with HiBFF's 9 specialist AvA tutors — each one built, safety-checked and topic-locked by us. Teens can customise how a tutor looks and sounds, but they can **never create their own unvetted AI characters**.

**Time Limits:**
Set daily usage limits (like 2 hours per day) and quiet hours (like no notifications after 10pm)

**Privacy Settings:**
Decide what information you can see about your teen's usage

**Crisis Alerts:**
Get notified by text message and email if your teen clicks the Crisis Help button or if we detect concerning mood patterns

**To change these settings:**
Visit **parent.hibff.com** and log in with the password you created during setup`,
    relatedArticles: ['teen-requests', 'conversation-privacy']
  },
  {
    id: 'teen-requests',
    category: 'parents',
    title: 'Can my teen request changes to their settings?',
    content: `Yes! We believe in transparency and communication between parents and teens.

Your teen can see exactly what you are monitoring by going to "Parent Dashboard" in their menu. If they feel the settings are too restrictive, they can send you a request to:
- Get more time
- Access blocked features
- Increase privacy

You will receive an email with their request and their reason. You can approve or deny it from the email or by logging into parent.hibff.com.

**Important:** No matter what controls are active, the Crisis Help button always works. Your teen's safety comes first, always.`,
    relatedArticles: ['parental-controls']
  },

  // CATEGORY: Technical
  {
    id: 'technical-issues',
    category: 'technical',
    title: 'HiBFF is not loading or keeps crashing. What should I do?',
    content: `Try these steps:

**Step 1:** Close HiBFF completely and reopen it.

**Step 2:** Make sure you have a strong internet connection. HiBFF needs internet to work.

**Step 3:** Clear your browser cache:
- Click the three dots in the top right corner
- Go to **Settings → Privacy and Security → Clear Browsing Data**
- Select "Cached images and files"
- Click "Clear data"

**Step 4:** Try a different browser. HiBFF works best on Chrome, Firefox, Safari, and Edge.

**Step 5:** Check if HiBFF is down by going to **status.hibff.com**

**If none of this works, email support@hibff.com with:**
- What device you are using
- What browser you are using
- What you were doing when the problem happened
- A screenshot if possible`,
    relatedArticles: ['reset-password', 'contact-support']
  },
  {
    id: 'reset-password',
    category: 'technical',
    title: 'I forgot my password. How do I reset it?',
    content: `**Step 1:** Go to the login page at **hibff.com/login**

**Step 2:** Click "Forgot Password?" below the password box.

**Step 3:** Enter your email address.

**Step 4:** Check your email. You will get a message with a link to reset your password.

**Step 5:** Click the link and create a new password.

**Step 6:** Go back to hibff.com/login and sign in with your new password.

**Did not get the email?** Check your spam folder. Still nothing? Email **support@hibff.com** and we will help you.`,
    relatedArticles: ['technical-issues', 'contact-support']
  },

  // CATEGORY: Contact
  {
    id: 'contact-support',
    category: 'contact',
    title: 'How do I contact HiBFF support?',
    content: `We are here to help! You can reach us:

**Maya Helps:** Tap the "Maya Helps" button at the bottom of the Help & Support page — Maya will leave a message with next steps in your Notifications tab at the top of your dashboard.

**Email:** support@hibff.com
(We respond within 24 hours)

**Help Center:** You are here! Search through articles to find answers to common questions.

**For account issues, billing questions, or technical problems, please include:**
- Your email address
- Your subscription level
- A description of the problem
- Screenshots if relevant

**For urgent safety concerns, please use the Crisis Help button or call emergency services.**`,
    relatedArticles: ['technical-issues', 'crisis-help']
  }
];

// Search function
export const searchArticles = (query) => {
  if (!query || query.trim() === '') return [];

  const lowerQuery = query.toLowerCase();

  return helpArticles.filter(article => {
    return (
      article.title.toLowerCase().includes(lowerQuery) ||
      article.content.toLowerCase().includes(lowerQuery) ||
      helpCategories.find(cat => cat.id === article.category)?.name.toLowerCase().includes(lowerQuery)
    );
  });
};

// Get articles by category
export const getArticlesByCategory = (categoryId) => {
  return helpArticles.filter(article => article.category === categoryId);
};

// Get single article
export const getArticle = (articleId) => {
  return helpArticles.find(article => article.id === articleId);
};

// Get category info
export const getCategory = (categoryId) => {
  return helpCategories.find(cat => cat.id === categoryId);
};
